// =============================================================================
// ORDER TRANSFORM SERVICE
// Single source of truth for transforming orders between Supabase and app formats
// Both cloudDataService.ts and supabase/orders.ts should use these functions
// =============================================================================

import { logger } from '../utils/logger';

const log = logger.module('OrderTransform');

// =============================================================================
// HELPER: Strip base64 image data to prevent PostgreSQL timeout (error 57014)
// =============================================================================

export function stripImageData(obj: any): any {
  if (!obj) return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => stripImageData(item));
  }
  if (typeof obj === 'object') {
    const stripped: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Skip keys that typically contain base64 image data
      if (key === 'imageData' || key === 'image' || key === 'dataUrl') {
        continue;
      }
      // Check if value looks like base64 image data
      if (typeof value === 'string' && value.startsWith('data:image/')) {
        continue;
      }
      // Strip renders array images but keep structure
      if (key === 'renders' && Array.isArray(value)) {
        stripped[key] = value.map((r: any) => stripImageData(r));
        continue;
      }
      // Strip floor_plan_capture if it's large base64
      if (key === 'floor_plan_capture' && typeof value === 'string' && value.length > 1000) {
        stripped[key] = null;
        continue;
      }
      stripped[key] = stripImageData(value);
    }
    return stripped;
  }
  return obj;
}

// =============================================================================
// CANONICAL TRANSFORM: Supabase DB Row → App Format
// This is THE authoritative transform function
// =============================================================================

export function transformOrderFromSupabase(dbOrder: any): any {
  if (!dbOrder) return null;

  // Extract lockedSpecs with fallback
  const lockedSpecs = dbOrder.locked_specs || { placedBeds: dbOrder.beds || [] };

  // Extract woodType and finish from DEDICATED COLUMNS first, then fallback
  const woodType = dbOrder.wood_type ||
                   lockedSpecs?.woodType ||
                   lockedSpecs?.materials?.wood ||
                   dbOrder.wood ||
                   dbOrder.pricing?.woodType ||
                   null;

  const finish = dbOrder.finish ||
                 lockedSpecs?.finish ||
                 lockedSpecs?.materials?.finish ||
                 dbOrder.pricing?.finish ||
                 null;

  // Extract room dimensions from DEDICATED COLUMN first, then fallback
  const roomDimensions = dbOrder.room_dimensions ||
                          lockedSpecs?.roomDimensions ||
                          { width: 0, height: 0, length: 0 };

  // Extract renders and floor plan
  const renders = dbOrder.renders || lockedSpecs?.renders || [];
  const floorPlanCapture = dbOrder.floor_plan_capture || lockedSpecs?.floorPlanCapture || null;
  const addOns = dbOrder.add_ons || lockedSpecs?.addOns || {};

  // Extract payment data from JSONB column
  const payment = dbOrder.payment || {};

  // Beds - check multiple locations
  const beds = dbOrder.beds || lockedSpecs?.placedBeds || [];

  return {
    // ==========================================================================
    // CORE IDENTIFIERS
    // ==========================================================================
    id: dbOrder.id,                    // Supabase UUID
    uuid: dbOrder.id,                  // Alias for clarity
    orderId: dbOrder.order_id,         // Custom order number like "ORD-xxxxx"
    orderNumber: dbOrder.order_id,     // Alias
    orderDate: dbOrder.order_date,

    // ==========================================================================
    // CLIENT DATA
    // ==========================================================================
    client: dbOrder.client || {},
    clientId: dbOrder.client_id,

    // ==========================================================================
    // STATUS FIELDS
    // ==========================================================================
    status: dbOrder.status,
    status_obj: dbOrder.status_obj || {},
    pipelineStage: dbOrder.pipeline_stage,
    pipeline: dbOrder.pipeline || {},
    productionStage: dbOrder.production_stage,
    fabricationStatus: dbOrder.fabrication_status,

    // ==========================================================================
    // PAYMENT TRACKING
    // ==========================================================================
    payment: payment,
    depositPaid: payment.depositPaid ||
                 (payment.totalPaid && payment.totalPaid > 0) ||
                 (dbOrder.pipeline_stage && dbOrder.pipeline_stage !== 'awaiting_deposit' && dbOrder.pipeline_stage !== 'quote_sent') ||
                 false,
    balancePaid: payment.balancePaid || false,
    depositAmount: payment.depositAmount,
    balanceAmount: payment.balanceAmount,

    // ==========================================================================
    // ASSIGNMENT FIELDS
    // ==========================================================================
    requestedBy: dbOrder.requested_by,
    assignedDesigner: dbOrder.assigned_designer,
    assignedDesignerPhone: dbOrder.assigned_designer_phone,
    assignedPartnerId: dbOrder.assigned_partner_id,
    assignedPartnerName: dbOrder.assigned_partner_name,

    // ==========================================================================
    // PARTNER CLAIM FIELDS (both naming conventions for compatibility)
    // ==========================================================================
    partnerId: dbOrder.claimed_by_partner,
    partnerName: dbOrder.claimed_by_partner_name,
    claimedByPartner: dbOrder.claimed_by_partner,
    claimedByPartnerName: dbOrder.claimed_by_partner_name,
    claimedByPartnerAt: dbOrder.claimed_by_partner_at,
    claimedByPartnerLocation: dbOrder.claimed_by_partner_location,

    // ==========================================================================
    // PARTNER TARGETING FIELDS (direct assignment)
    // ==========================================================================
    targetPartnerId: dbOrder.target_partner_id,
    targetPartnerName: dbOrder.target_partner_name,
    releaseMode: dbOrder.release_mode,
    releasedToPartnersAt: dbOrder.released_to_partners_at,

    // ==========================================================================
    // FABRICATION/PAYOUT FIELDS
    // ==========================================================================
    fabPayout: dbOrder.fab_payout,
    fabPayoutAmount: dbOrder.fab_payout,

    // ==========================================================================
    // BIDDING FIELDS
    // ==========================================================================
    biddingEndsAt: dbOrder.bidding_ends_at,
    bids: dbOrder.bids || [],
    winningBid: dbOrder.winning_bid,

    // ==========================================================================
    // RECALL FIELDS
    // ==========================================================================
    recallReason: dbOrder.recall_reason,
    recalledAt: dbOrder.recalled_at,

    // ==========================================================================
    // PARTNER WORKFLOW FIELDS
    // ==========================================================================
    workStartedAt: dbOrder.work_started_at,
    pausedAt: dbOrder.paused_at,
    pauseReason: dbOrder.pause_reason,
    statusBeforePause: dbOrder.status_before_pause,
    resumedAt: dbOrder.resumed_at,
    returnedByPartner: dbOrder.returned_by_partner,
    returnedByPartnerName: dbOrder.returned_by_partner_name,
    returnedAt: dbOrder.returned_at,
    returnReason: dbOrder.return_reason,
    partnerMessages: dbOrder.partner_messages || [],
    hasUnreadPartnerQuestion: dbOrder.has_unread_partner_question || false,
    lastPartnerQuestionAt: dbOrder.last_partner_question_at,

    // ==========================================================================
    // DESIGN DATA - TOP LEVEL
    // ==========================================================================
    beds: beds,
    placedBeds: beds,
    roomDimensions: roomDimensions,
    roomLength: roomDimensions.length || roomDimensions.width,
    roomWidth: roomDimensions.width || roomDimensions.height,
    woodType: woodType,
    wood: woodType,
    finish: finish,
    renders: renders,
    floorPlanCapture: floorPlanCapture,
    addOns: addOns,
    config: lockedSpecs?.config || dbOrder.config || null,
    pricing: dbOrder.pricing || {},

    // ==========================================================================
    // LOCKED SPECS (complete snapshot)
    // ==========================================================================
    lockedSpecs: {
      ...lockedSpecs,
      placedBeds: lockedSpecs?.placedBeds || beds
    },

    // ==========================================================================
    // SIDEBAR STATE FIELDS
    // ==========================================================================
    bedConfigs: lockedSpecs?.bedConfigs || dbOrder.bed_configs || [],
    discount: lockedSpecs?.discount || dbOrder.discount || null,
    deliveryZone: lockedSpecs?.deliveryZone || dbOrder.delivery_zone || 'local',
    wantsInstall: lockedSpecs?.wantsInstall ?? dbOrder.wants_install ?? false,
    guardrailStyle: lockedSpecs?.guardrailStyle || dbOrder.guardrail_style,
    guardrailCoverage: lockedSpecs?.guardrailCoverage || dbOrder.guardrail_coverage,
    guardrailConfigs: lockedSpecs?.guardrailConfigs || dbOrder.guardrail_configs || [],
    guardrailTemplate: lockedSpecs?.guardrailTemplate || dbOrder.guardrail_template,

    // ==========================================================================
    // CUTLIST & WORKFLOW DATA
    // ==========================================================================
    cutlistData: dbOrder.cutlist_data || null,
    workflowSteps: dbOrder.workflow_steps || null,

    // ==========================================================================
    // CLIENT SIGNATURE & APPROVAL
    // ==========================================================================
    clientSignature: dbOrder.client_signature || null,
    designApprovedAt: dbOrder.design_approved_at || null,
    designApprovedBy: dbOrder.design_approved_by || null,
    approvalStatus: dbOrder.approval_status || null,

    // ==========================================================================
    // RELEASE TO PRODUCTION FIELDS
    // ==========================================================================
    releasedToProduction: dbOrder.released_to_production || false,
    releasedToProductionAt: dbOrder.released_to_production_at || null,
    releasedToProductionBy: dbOrder.released_to_production_by || null,
    productionVersion: dbOrder.production_version || 0,
    sentToProductionAt: dbOrder.sent_to_production_at || null,
    productionHistory: dbOrder.production_history || [],

    // ==========================================================================
    // JOB COSTING FIELDS
    // ==========================================================================
    materialActuals: dbOrder.material_actuals || null,
    budgetActuals: dbOrder.budget_actuals || null,
    shippingActuals: dbOrder.shipping_actuals || null,
    budgetAllocation: dbOrder.budget_allocation || null,
    jobCosting: dbOrder.job_costing || null,
    targetMargin: dbOrder.target_margin,

    // ==========================================================================
    // PRICING ADJUSTMENT FIELDS
    // ==========================================================================
    adjustedPrice: dbOrder.adjusted_price,
    originalPrice: dbOrder.original_price,
    priceAdjustment: dbOrder.price_adjustment,

    // ==========================================================================
    // TRACKING & SCHEDULING
    // ==========================================================================
    trackingStatus: dbOrder.tracking_status,
    dueDate: dbOrder.due_date,
    installTentativeDate: dbOrder.install_tentative_date,
    installConfirmedDate: dbOrder.install_confirmed_date,
    installRescheduleDates: dbOrder.install_reschedule_dates || [],
    installNotes: dbOrder.install_notes,

    // ==========================================================================
    // DELIVERY & INSTALLATION
    // ==========================================================================
    delivery: dbOrder.delivery || {},
    installation: dbOrder.installation || {},

    // ==========================================================================
    // CHANGE ORDERS & MESSAGES
    // ==========================================================================
    changeOrders: dbOrder.change_orders || [],
    messages: dbOrder.messages || [],

    // ==========================================================================
    // INVOICING & COMMISSION
    // ==========================================================================
    invoiceNumber: dbOrder.invoice_number,
    invoiceSentAt: dbOrder.invoice_sent_at,
    commission: dbOrder.commission || {},

    // ==========================================================================
    // MISC FIELDS
    // ==========================================================================
    notes: dbOrder.notes || [],
    documents: dbOrder.documents || {},
    timeline: dbOrder.timeline || [],
    scrapAllocations: dbOrder.scrap_allocations,
    adminActions: dbOrder.admin_actions || [],
    createdAt: dbOrder.created_at,
    updatedAt: dbOrder.updated_at,
    createdBy: dbOrder.created_by
  };
}

// =============================================================================
// CANONICAL TRANSFORM: App Format → Supabase DB Row
// =============================================================================

export function transformOrderToSupabase(order: any): Record<string, any> {
  // Extract all data from multiple possible locations FIRST
  const placedBeds = order.lockedSpecs?.placedBeds || order.beds || order.placedBeds || [];
  const roomDimensions = order.roomDimensions ||
                          order.lockedSpecs?.roomDimensions ||
                          { width: order.roomWidth || 0, height: order.roomHeight || 0, length: order.roomLength || 0 };
  const woodType = order.woodType || order.wood || order.lockedSpecs?.woodType || order.pricing?.woodType || null;
  const finish = order.finish || order.lockedSpecs?.finish || order.pricing?.finish || null;
  const renders = stripImageData(order.renders || order.lockedSpecs?.renders || null);
  const floorPlanCapture = stripImageData(order.floorPlanCapture || order.lockedSpecs?.floorPlanCapture || null);
  const addOns = order.addOns || order.lockedSpecs?.addOns || null;

  // Build base payload
  const payload: Record<string, any> = {
    order_id: order.orderId || order.id || order.orderNumber,
    client: order.client || {},
    beds: placedBeds,
    pricing: order.pricing || order.financials || {},
    status: order.status || 'pending',
    production_stage: order.productionStage,
    bids: order.bids || [],

    // Locked specs - merge top-level fields back in
    locked_specs: stripImageData({
      ...(order.lockedSpecs || {}),
      placedBeds: placedBeds,
      roomDimensions: roomDimensions,
      woodType: woodType,
      finish: finish,
      addOns: addOns,
      discount: order.discount ?? order.lockedSpecs?.discount ?? null,
      deliveryZone: order.deliveryZone ?? order.lockedSpecs?.deliveryZone ?? 'local',
      wantsInstall: order.wantsInstall ?? order.lockedSpecs?.wantsInstall ?? false,
      bedConfigs: order.bedConfigs ?? order.lockedSpecs?.bedConfigs ?? [],
      guardrailStyle: order.guardrailStyle ?? order.lockedSpecs?.guardrailStyle,
      guardrailCoverage: order.guardrailCoverage ?? order.lockedSpecs?.guardrailCoverage,
      guardrailConfigs: order.guardrailConfigs ?? order.lockedSpecs?.guardrailConfigs ?? [],
      guardrailTemplate: order.guardrailTemplate ?? order.lockedSpecs?.guardrailTemplate
    }),

    // Design data columns
    room_dimensions: roomDimensions,
    wood_type: woodType,
    finish: finish,
    renders: renders,
    floor_plan_capture: floorPlanCapture,
    add_ons: addOns,

    // Scheduling
    due_date: order.dueDate,
    notes: order.notes,
    documents: order.documents || {},
    timeline: order.timeline || [],

    // Job costing
    material_actuals: order.materialActuals || null,
    budget_actuals: order.budgetActuals || null,
    shipping_actuals: order.shippingActuals || null,
    budget_allocation: order.budgetAllocation || null,
    job_costing: order.jobCosting || null,

    // Cutlist & workflow
    cutlist_data: order.cutlistData || order.cutlist_data || null,
    workflow_steps: order.workflowSteps || order.workflow_steps || null,

    // Client signature
    client_signature: order.clientSignature || order.client_signature || null,
    design_approved_at: order.designApprovedAt || order.design_approved_at || null,
    design_approved_by: order.designApprovedBy || order.design_approved_by || null,
    approval_status: order.approvalStatus || order.approval_status || null
  };

  // ==========================================================================
  // CONDITIONAL FIELDS: Only include if explicitly defined
  // This prevents auto-saves from clearing partner claims
  // ==========================================================================

  if (order.fabricationStatus !== undefined) {
    payload.fabrication_status = order.fabricationStatus;
  }
  if (order.pipelineStage !== undefined) {
    payload.pipeline_stage = order.pipelineStage;
  }

  // Partner claim fields - only if explicitly set
  // CRITICAL: claimed_by_partner has FK constraint to partners table (UUID PKs)
  // Never write non-UUID values (e.g. slugs like "choctaw-beach")
  if (order.claimedByPartner !== undefined) {
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(order.claimedByPartner || '');
    payload.claimed_by_partner = isUUID ? order.claimedByPartner : null;
  }
  if (order.claimedByPartnerName !== undefined) {
    payload.claimed_by_partner_name = order.claimedByPartnerName;
  }
  if (order.claimedByPartnerAt !== undefined) {
    payload.claimed_by_partner_at = order.claimedByPartnerAt;
  }
  if (order.claimedByPartnerLocation !== undefined) {
    payload.claimed_by_partner_location = order.claimedByPartnerLocation;
  }

  // Target partner fields - only if explicitly set
  if (order.targetPartnerId !== undefined) {
    payload.target_partner_id = order.targetPartnerId;
  }
  if (order.targetPartnerName !== undefined) {
    payload.target_partner_name = order.targetPartnerName;
  }
  if (order.releaseMode !== undefined) {
    payload.release_mode = order.releaseMode;
  }
  if (order.releasedToPartnersAt !== undefined) {
    payload.released_to_partners_at = order.releasedToPartnersAt;
  }

  // Fabrication payout
  if (order.fabPayout !== undefined) {
    payload.fab_payout = order.fabPayout;
  }

  // Release to production fields
  if (order.releasedToProduction !== undefined) {
    payload.released_to_production = order.releasedToProduction;
  }
  if (order.releasedToProductionAt !== undefined) {
    payload.released_to_production_at = order.releasedToProductionAt;
  }

  return payload;
}

// =============================================================================
// EXPORTS
// =============================================================================

export const OrderTransformService = {
  transformOrderFromSupabase,
  transformOrderToSupabase,
  stripImageData
};

export default OrderTransformService;

